import React, { useState, memo, useCallback, useRef } from "react";
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  Platform,
  useWindowDimensions,
} from "react-native";
import { platformAlert } from "../../utils/platformAlert";
import { Image } from "expo-image";
import { Feather } from "@expo/vector-icons";
import { spacing, typography } from "../../theme";
import { useColors, ThemeColors } from '../../hooks/useColors';
import type { Product, Vendor } from "../../types/supabase";
import { useLikes } from "../../hooks/useLikes";
import { useAuth } from "../../hooks/useAuth";
import { useToast } from "../../hooks/useToast";
import { useVendorProducts } from "../../hooks/useVendorProducts";
import { formatPrice } from "../../utils/format";
import { useGuestMode } from "../../contexts/GuestModeContext";

// Max width for phone container on web
const PHONE_CONTAINER_MAX_WIDTH = 430;

interface ProductCardProps {
  product: Product;
  onPress?: () => void;
  isSellerView?: boolean;
  cardWidth?: number;
  vendor?: Vendor;
}

export const ProductCard = memo(
  function ProductCard({
    product,
    onPress,
    isSellerView,
    cardWidth,
    vendor,
  }: ProductCardProps) {
    const colors = useColors();
    const styles = createStyles(colors);
    const { user } = useAuth();
    const { toggleLike, isLiked } = useLikes();
    const { show } = useToast();
    const { updateProductQuantity } = useVendorProducts();
    const { requireAuth } = useGuestMode();
    const liked = isLiked(product.id);
    const [activeImageIndex, setActiveImageIndex] = useState(0);
    
    // Get responsive window dimensions
    const windowDimensions = useWindowDimensions();
    
    // On web, constrain width to phone container max width
    const effectiveWidth = Platform.OS === 'web' 
      ? Math.min(windowDimensions.width, PHONE_CONTAINER_MAX_WIDTH)
      : windowDimensions.width;

    const handleLike = useCallback(() => {
      // Check auth - show modal if guest
      if (!requireAuth('like products')) {
        return;
      }
      toggleLike(product.id);
    }, [product.id, toggleLike, requireAuth]);

    const handleScroll = useCallback((event: any) => {
      const slideSize = event.nativeEvent.layoutMeasurement.width;
      const index = Math.round(event.nativeEvent.contentOffset.x / slideSize);
      setActiveImageIndex(index);
    }, []);

    // Track if user is scrolling to prevent tap from firing
    const isScrollingRef = React.useRef(false);
    const scrollTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

    const handleScrollBeginDrag = useCallback(() => {
      isScrollingRef.current = true;
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
      scrollTimeoutRef.current = setTimeout(() => {
        isScrollingRef.current = false;
      }, 150);
    }, []);

    const handleImagePress = useCallback(() => {
      // Only trigger if not scrolling
      if (!isScrollingRef.current && onPress) {
        onPress();
      }
    }, [onPress]);

    const handleDelete = useCallback(() => {
      platformAlert(
        "Remove Product",
        "Are you sure you want to remove this product from your listings? This will set the inventory to 0.",
        [
          {
            text: "Cancel",
            style: "cancel",
          },
          {
            text: "Remove",
            style: "destructive",
            onPress: () => {
              updateProductQuantity({ productId: product.id, quantity: 0 });
              show({
                title: "Product Removed",
                message: "The product has been removed from your listings.",
                type: "success",
              });
            },
          },
        ]
      );
    }, [product.id, updateProductQuantity, show]);

    const productImages = product.images || [];
    const hasImages = productImages.length > 0;

    // Calculate card width for 2-column grid
    // Account for: container padding (lg * 2) + small gap between cards (4px)
    // Use effectiveWidth which is constrained to phone container on web
    const calculatedCardWidth = cardWidth || (effectiveWidth - spacing.lg * 2 - 4) / 2;

    // Dynamic styles based on card width
    const dynamicStyles = StyleSheet.create({
      card: {
        ...styles.card,
        width: calculatedCardWidth,
      },
      imageWrap: {
        ...styles.imageWrap,
        width: calculatedCardWidth,
        height: calculatedCardWidth,
      },
      image: {
        width: calculatedCardWidth,
        height: calculatedCardWidth,
        backgroundColor: colors.backgroundSecondary,
      },
    });

    return (
      <View style={dynamicStyles.card}>
        <View style={dynamicStyles.imageWrap}>
          {hasImages ? (
            <ScrollView
              horizontal
              pagingEnabled
              showsHorizontalScrollIndicator={false}
              onScroll={handleScroll}
              onScrollBeginDrag={handleScrollBeginDrag}
              scrollEventThrottle={16}
              decelerationRate="fast"
              snapToInterval={calculatedCardWidth}
              snapToAlignment="center"
              removeClippedSubviews
              scrollEnabled={productImages.length > 1}
            >
              {productImages.map((uri, idx) => (
                <TouchableOpacity
                  key={idx}
                  activeOpacity={0.95}
                  onPress={handleImagePress}
                  style={dynamicStyles.image}
                  delayPressIn={50}
                >
                  <Image
                    source={{ uri }}
                    style={{
                      width: "100%",
                      height: "100%",
                      backgroundColor: colors.backgroundSecondary,
                    }}
                    contentFit="cover"
                    cachePolicy="memory-disk"
                    transition={0}
                  />
                </TouchableOpacity>
              ))}
            </ScrollView>
          ) : (
            isSellerView ? (
              <View
                style={[dynamicStyles.image, styles.placeholderContainer]}
              >
                <Feather name="image" size={48} color={colors.textLight} />
                <Text style={styles.placeholderText}>No Image</Text>
              </View>
            ) : (
              <TouchableOpacity
                activeOpacity={0.9}
                onPress={onPress}
                style={[dynamicStyles.image, styles.placeholderContainer]}
              >
                <Feather name="image" size={48} color={colors.textLight} />
                <Text style={styles.placeholderText}>No Image</Text>
              </TouchableOpacity>
            )
          )}

          {productImages.length > 1 && (
            <View style={styles.pagination}>
              {productImages.map((_, idx) => (
                <View
                  key={idx}
                  style={[
                    styles.dot,
                    idx === activeImageIndex && styles.dotActive,
                  ]}
                />
              ))}
            </View>
          )}

          <View style={styles.priceTag}>
            <Text style={styles.price}>{formatPrice(product.price)}</Text>
          </View>

          {isSellerView ? (
            <View style={styles.actionButtons}>
              <TouchableOpacity
                style={styles.actionButton}
                onPress={(e) => {
                  e.stopPropagation();
                  handleDelete();
                }}
              >
                <Feather name="trash-2" size={16} color={colors.surface} />
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.actionButton}
                onPress={(e) => {
                  e.stopPropagation();
                  onPress?.();
                }}
              >
                <Feather name="edit-2" size={16} color={colors.surface} />
              </TouchableOpacity>
            </View>
          ) : (
            <TouchableOpacity
              style={[styles.likeBtn, liked && styles.likeBtnActive]}
              onPress={(e) => {
                e.stopPropagation();
                handleLike();
              }}
            >
              <Feather
                name="heart"
                size={20}
                color={liked ? colors.background : "rgba(0, 0, 0, 0.35)"}
              />
            </TouchableOpacity>
          )}
        </View>

        {isSellerView ? (
          <View style={styles.content}>
            <Text style={styles.title} numberOfLines={2}>
              {product.title}
            </Text>
          </View>
        ) : (
          <TouchableOpacity
            onPress={onPress}
            activeOpacity={0.9}
            style={styles.content}
          >
            <Text style={styles.title} numberOfLines={2}>
              {product.title}
            </Text>
          </TouchableOpacity>
        )}
      </View>
    );
  },
  (prev, next) =>
    prev.product.id === next.product.id &&
    prev.product.price === next.product.price &&
    prev.product.title === next.product.title &&
    JSON.stringify(prev.product.images) === JSON.stringify(next.product.images)
);

const createStyles = (colors: ThemeColors) => StyleSheet.create({
  card: {
    backgroundColor: colors.surface,
    borderRadius: 20,
    overflow: "hidden",
    marginHorizontal: spacing.xs,
    borderWidth: 1,
    borderColor: colors.border,
    // iOS shadow
    shadowColor: "#000",
    shadowOpacity: 0.03,
    shadowOffset: { width: 0, height: 2 },
    shadowRadius: 8,
    // Android shadow
    elevation: 2,
  },
  imageWrap: {
    position: "relative",
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    overflow: "hidden",
  },
  image: {
    backgroundColor: colors.backgroundSecondary,
  },
  pagination: {
    position: "absolute",
    bottom: spacing.sm,
    alignSelf: "center",
    flexDirection: "row",
    backgroundColor: "rgba(0,0,0,0.5)",
    paddingHorizontal: spacing.sm,
    paddingVertical: 4,
    borderRadius: 10,
  },
  dot: {
    width: 5,
    height: 5,
    borderRadius: 2.5,
    backgroundColor: "#fff",
    opacity: 0.5,
    marginHorizontal: 2,
  },
  dotActive: {
    opacity: 1,
    backgroundColor: colors.primary,
  },
  priceTag: {
    position: "absolute",
    top: spacing.sm,
    left: spacing.sm,
    backgroundColor: colors.primary,
    borderRadius: 12,
    paddingHorizontal: 8,
    paddingVertical: 4,
    shadowColor: colors.shadow,
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.2,
    shadowRadius: 3,
    elevation: 3,
  },
  price: {
    ...typography.body,
    fontWeight: "600",
    color: colors.background,
  },
  likeBtn: {
    position: "absolute",
    bottom: spacing.sm,
    right: spacing.sm,
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: "rgba(34, 197, 94, 0.15)",
    alignItems: "center",
    justifyContent: "center",
  },
  likeBtnActive: {
    backgroundColor: colors.primary,
  },
  content: {
    paddingHorizontal: spacing.sm,
    paddingVertical: 15,
    minHeight: 60, // Fixed minimum height to prevent layout shift on load
    justifyContent: "center",
  },
  title: {
    ...typography.body,
    fontWeight: "500",
    color: colors.text,
  },
  actionButtons: {
    position: "absolute",
    top: spacing.sm,
    right: spacing.sm,
    flexDirection: "column",
    gap: 4,
  },
  actionButton: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: colors.primary,
    alignItems: "center",
    justifyContent: "center",
    shadowColor: colors.shadow,
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.2,
    shadowRadius: 2,
    elevation: 3,
  },
  placeholderContainer: {
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: colors.backgroundSecondary,
    gap: spacing.sm,
  },
  placeholderText: {
    ...typography.caption,
    color: colors.textLight,
    fontWeight: "500",
  },
});
